/**
 * CopilotPanel — chat drawer for the FDR Copilot agent (per-entity).
 *
 * Talks to POST /api/fdr/entities/:id/copilot. Renders the answer, the
 * proactive gap flags, and which parts of the record the agent consulted —
 * the trace is the trust. History is held client-side and passed with each
 * turn (bounded server-side); every invocation is ledger-logged server-side.
 */
import React, { useState, useRef, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL;

const FLAG_CFG = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  warning:  'bg-amber-50 text-amber-700 border-amber-200',
  info:     'bg-blue-50 text-blue-700 border-blue-200',
};

const SUGGESTIONS = [
  'Where do we stand with this vendor overall?',
  'What would a CMS auditor flag here today?',
  'Draft the oversight summary for the compliance committee.',
  'Which contract provisions are weak or missing?',
];

export default function CopilotPanel({ entityId, entityName, onClose }) {
  const [messages, setMessages] = useState([]); // {role, content, flags?, consulted?}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput('');
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/fdr/entities/${entityId}/copilot`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, data.success
        ? { role: 'assistant', content: data.answer, flags: data.flags, consulted: data.consulted }
        : { role: 'assistant', content: data.message || 'Copilot failed — try again.', error: true }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Copilot failed — try again.', error: true }]);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-[440px] max-w-full bg-white border-l border-slate-200 shadow-2xl flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 flex-shrink-0">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-800">Oversight Copilot</div>
          <div className="text-xs text-slate-400 truncate">{entityName} · reads the record, decides nothing</div>
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="pt-6">
            <p className="text-sm text-slate-500 leading-relaxed">
              Ask anything about this vendor. The copilot reads the live record — contracts, assessments,
              findings, screenings, attestations — and answers only from what it finds, flagging gaps as it goes.
            </p>
            <div className="mt-4 space-y-2">
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)}
                  className="block w-full text-left text-xs text-slate-600 bg-slate-50 hover:bg-orange-50 hover:text-orange-700 rounded-xl px-3.5 py-2.5 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => m.role === 'user' ? (
          <div key={i} className="flex justify-end">
            <div className="max-w-[85%] bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5">
              {m.content}
            </div>
          </div>
        ) : (
          <div key={i} className="space-y-2">
            <div className={`max-w-[95%] text-sm rounded-2xl rounded-bl-md px-4 py-3 whitespace-pre-wrap leading-relaxed ${
              m.error ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-700'}`}>
              {m.content}
            </div>
            {m.flags?.length > 0 && (
              <div className="space-y-1.5">
                {m.flags.map((f, j) => (
                  <div key={j} className={`text-xs border rounded-xl px-3 py-2 ${FLAG_CFG[f.severity] || FLAG_CFG.info}`}>
                    <span className="font-bold uppercase text-[10px] tracking-wide mr-1.5">{f.severity}</span>
                    {f.note}
                  </div>
                ))}
              </div>
            )}
            {m.consulted?.length > 0 && (
              <div className="text-[10px] text-slate-400 pl-1">
                Consulted: {m.consulted.map(t => t.replace('get_', '').replace(/_/g, ' ')).join(' · ')}
              </div>
            )}
          </div>
        ))}

        {busy && (
          <div className="flex items-center gap-2 text-xs text-slate-400 pl-1">
            <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
            Reading the record…
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="px-5 py-4 border-t border-slate-100 flex-shrink-0">
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about this vendor…" disabled={busy}
            className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 disabled:bg-slate-50" />
          <button onClick={() => send()} disabled={busy || !input.trim()}
            className="px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-40 disabled:shadow-none transition-all">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          </button>
        </div>
        <p className="text-[10px] text-slate-300 mt-2">Decision support only. Every consult is ledger-logged.</p>
      </div>
    </div>
  );
}
